import React from 'react';
import { t } from 'i18next';
import type { CSSProperties } from 'react';
import { GameType } from '@sparted/shared-library/business/types';

import UIcon from 'Components/unit/UIcon/UIcon';
import { COLORS } from 'Components/foundation';
import { blendHexColors } from 'Components/foundation/colors';

import styles from './UGameplayIndicator.style';

export type UGameplayIndicatorProps = {
  style?: CSSProperties;
  type: GameType;
  hideLabel?: boolean;
};

const useGameplayDisplay = (type: GameType) => {
  const translations = {
    'swiping-cards': t('unit_components:u_gameplay_indicator.swiping_cards'),
    'picture-spot': t('unit_components:u_gameplay_indicator.picture_spot'),
    'open-question': t('unit_components:u_gameplay_indicator.open_question'),
    'no-interaction': t('unit_components:u_gameplay_indicator.no_interaction'),
    mcq: t('unit_components:u_gameplay_indicator.qcm'),
    'error-text': t('unit_components:u_gameplay_indicator.error_text'),
    'ordering-cards': t('unit_components:u_gameplay_indicator.ordering_cards'),
  };

  const colors = {
    'swiping-cards': COLORS.GAMEPLAY.SWIPING_CARDS,
    'picture-spot': COLORS.GAMEPLAY.PICTURE_SPOT,
    'open-question': COLORS.GAMEPLAY.OPEN_QUESTION,
    'no-interaction': COLORS.GAMEPLAY.NO_INT_CONTENT,
    mcq: COLORS.GAMEPLAY.QCM,
    'error-text': COLORS.GAMEPLAY.ERROR_TEXT,
    'ordering-cards': COLORS.GAMEPLAY.ORDERING_CARDS,
  };

  return {
    label: translations[type] || type,
    color: colors[type] || COLORS.TEXT.SECONDARY_DEFAULT,
  };
};

/**
 * UGameplayIndicator
 * type: type of the indicator
 * style: style of the component
 */
export const UGameplayIndicator = ({ style = {}, type, hideLabel = false }: UGameplayIndicatorProps) => {
  const { label, color } = useGameplayDisplay(type);
  const transparentBackground = `${color}33`;

  return (
    <div style={{ ...styles.wrapper, ...style }}>
      <div style={{ ...styles.iconBackground, backgroundColor: transparentBackground }}>
        <UIcon color={blendHexColors(color, COLORS.TEXT.DEFAULT)} name={type} size={12} style={styles.icon} />
      </div>
      {!hideLabel && (
        <div data-test-id="GAMEPLAY_LABEL" style={styles.text}>
          {label}
        </div>
      )}
    </div>
  );
};

export default UGameplayIndicator;
